import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import {
  Home, MapPin, Check, Gift, Loader2, Sparkles, Plus, Minus, AlertCircle
} from "lucide-react";
import {
  SERVICE_ICONS, CALLOUT_POSITIONS, SERVICE_ORDER, getWindowPricePerUnit,
  PATIO_SIZES, DECK_SIZES, FENCE_TIERS, CHIMNEY_CONDITIONS,
  getWalkwayPrice, getSidingUpcharge, getSidingUpchargeLabel
} from "./constants";

export default function ServiceSelectionStep({
  pricingConfig,
  selectedServices,
  toggleService,
  calculatedPrices,
  propertyImages,
  showStreetView,
  setShowStreetView,
  getFullAddress,
  propertyDetails,
  // Service-specific state
  windowCount,
  setWindowCount,
  fenceLinearFeet,
  setFenceLinearFeet,
  walkwayLinearFeet,
  setWalkwayLinearFeet,
  sidewalkLinearFeet,
  setSidewalkLinearFeet,
  patioSize,
  setPatioSize,
  deckSize,
  setDeckSize,
  chimneyCondition,
  setChimneyCondition,
  sidingType,
}) {
  const selectedCount = Object.values(selectedServices).filter(Boolean).length;
  const showBundlePromo = selectedCount === 1 && pricingConfig;
  const sqft = propertyDetails?.verified_sqft || propertyDetails?.square_footage || 2000;

  // Sort services by the defined order
  const getSortedServices = () => {
    if (!pricingConfig?.services) return [];
    return [...pricingConfig.services].sort((a, b) => {
      const aIndex = SERVICE_ORDER.indexOf(a.service_id);
      const bIndex = SERVICE_ORDER.indexOf(b.service_id);
      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    });
  };

  // Get bundle suggestions
  const getBundleSuggestions = () => {
    if (!pricingConfig || selectedCount === 0) return [];
    const sortedServices = getSortedServices();
    const unselected = sortedServices.filter((s) => 
      !selectedServices[s.service_id] && 
      calculatedPrices[s.service_id]?.original > 0
    );
    
    const nextDiscountTier = pricingConfig.bundle_discounts?.find(
      bd => bd.min_services === selectedCount + 1
    );
    const discountPercent = nextDiscountTier?.discount_percent || 5;
    
    return unselected.slice(0, 2).map((service) => {
      const originalPrice = calculatedPrices[service.service_id]?.original || 0;
      const bundlePrice = originalPrice * (1 - discountPercent / 100);
      return {
        ...service,
        bundlePrice: Math.round(bundlePrice),
        discountPercent,
      };
    });
  };

  // Get service note with any additional info
  const getServiceNote = (service) => {
    if (service.service_id === 'driveway') {
      return "Additional charges apply for stain removal";
    }
    if (service.service_id === 'sidewalk') {
      return "Additional charges apply for stain removal";
    }
    if (service.service_id === 'gutters') {
      return "⚠️ Open gutters only - does not include gutters with screens";
    }
    if (service.service_id === 'windows') {
      return "⚠️ All exterior window screens must be removed by property owner prior to cleaning";
    }
    if (service.service_id === 'siding' && sidingType !== 'standard') {
      return getSidingUpchargeLabel(sidingType, sqft);
    }
    return service.notes;
  };
  
  // Check if service has a warning note
  const isWarningNote = (serviceId) => {
    return serviceId === 'gutters' || serviceId === 'windows';
  };

  return (
    <div className="animate-fade-in-up" data-testid="step-2-services">
      <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
        Select your services
      </h2>
      <p className="text-slate-600 mb-8">Click on the areas you'd like us to clean</p>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Property View with Callouts */}
        <div className="relative">
          <Card className="border-slate-200 overflow-hidden">
            <div className="relative h-[400px] bg-slate-100">
              {propertyImages.loading ? (
                <div className="w-full h-full flex items-center justify-center bg-slate-200">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  <span className="ml-2 text-slate-600">Loading property images...</span>
                </div>
              ) : propertyImages.satelliteUrl || propertyImages.streetviewUrl ? (
                <>
                  <img
                    src={showStreetView ? propertyImages.streetviewUrl : propertyImages.satelliteUrl}
                    alt={showStreetView ? "Street view" : "Satellite view"}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = "https://images.unsplash.com/photo-1568605114967-8130f3a36994?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80";
                    }}
                  />
                  {/* View Toggle Buttons - Street View on left, Overhead View on right */}
                  <div className="absolute top-3 left-3 z-20">
                    <Button
                      size="sm"
                      onClick={() => setShowStreetView(true)}
                      className={showStreetView 
                        ? "bg-blue-600 hover:bg-blue-700 text-white text-xs shadow-lg" 
                        : "bg-white/95 hover:bg-white text-slate-700 text-xs border border-slate-300 shadow-lg"}
                    >
                      <Home className="w-3 h-3 mr-1" />
                      Street View
                    </Button>
                  </div>
                  <div className="absolute top-3 right-3 z-20">
                    <Button
                      size="sm"
                      onClick={() => setShowStreetView(false)}
                      className={!showStreetView 
                        ? "bg-blue-600 hover:bg-blue-700 text-white text-xs shadow-lg" 
                        : "bg-white/95 hover:bg-white text-slate-700 text-xs border border-slate-300 shadow-lg"}
                    >
                      <MapPin className="w-3 h-3 mr-1" />
                      Overhead View
                    </Button>
                  </div>
                  {/* Address Label */}
                  <div className="absolute bottom-3 left-3 bg-black/70 text-white px-3 py-1 rounded-lg text-sm">
                    {getFullAddress()}
                  </div>
                </>
              ) : (
                <img
                  src="https://images.unsplash.com/photo-1568605114967-8130f3a36994?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                  alt="Property view"
                  className="w-full h-full object-cover"
                />
              )}
              {/* Service Callouts - Only show on satellite view */}
              {!showStreetView && getSortedServices().map((service) => {
                const position = CALLOUT_POSITIONS[service.service_id];
                const isSelected = selectedServices[service.service_id];
                const price = calculatedPrices[service.service_id];
                
                const shortName = {
                  'siding': 'Siding',
                  'driveway': 'Driveway',
                  'windows': 'Windows',
                  'gutters': 'Gutters',
                  'entries': 'Entries',
                  'sidewalk': 'Sidewalk',
                  'patio': 'Patio',
                  'deck': 'Deck',
                  'fence': 'Fence',
                  'roof': 'Roof',
                  'chimney': 'Chimney'
                }[service.service_id] || service.service_name;

                if (!position) return null;

                return (
                  <button
                    key={service.service_id}
                    onClick={() => toggleService(service.service_id)}
                    className={`map-callout ${isSelected ? 'selected' : ''}`}
                    style={position}
                    data-testid={`callout-${service.service_id}`}
                  >
                    <span>{shortName}</span>
                    {price && !price.requiresQuote && (
                      <span className="ml-1 font-bold">${price.discounted}</span>
                    )}
                    {isSelected && <Check className="w-3 h-3 ml-1 inline text-blue-600" />}
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Bundle Promo */}
          {showBundlePromo && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
              <div className="flex items-start gap-3">
                <Gift className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-800">Bundle & Save!</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Add another service to unlock bundle discounts on all services!
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Service List */}
        <div className="space-y-4">
          {!pricingConfig ? (
            <>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-24 rounded-xl" />
              ))}
            </>
          ) : (
            getSortedServices().map((service) => {
              const Icon = SERVICE_ICONS[service.service_id] || Home;
              const isSelected = selectedServices[service.service_id];
              const price = calculatedPrices[service.service_id];
              const hasCustomSelector = ['windows', 'fence', 'patio', 'deck', 'chimney', 'entries', 'sidewalk'].includes(service.service_id);
              const serviceNote = getServiceNote(service);

              return (
                <Card
                  key={service.service_id}
                  className={`border-2 transition-all cursor-pointer ${
                    isSelected ? 'border-blue-500 bg-blue-50/50' : 'border-slate-100 hover:border-slate-200'
                  }`}
                  onClick={() => toggleService(service.service_id)}
                  data-testid={`service-card-${service.service_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleService(service.service_id)}
                          className="h-5 w-5"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`checkbox-${service.service_id}`}
                        />
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-blue-600' : 'bg-slate-100'
                        }`}>
                          <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-slate-600'}`} />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{service.service_name}</p>
                          {service.service_id === 'windows' && (
                            <p className="text-xs text-slate-500">
                              ${getWindowPricePerUnit(windowCount)}/window ({windowCount <= 15 ? '1-15' : windowCount <= 25 ? '16-25' : '26+'} tier)
                            </p>
                          )}
                          {serviceNote && (
                            <p className={`text-xs ${
                              isWarningNote(service.service_id) 
                                ? 'text-amber-600 font-medium' 
                                : service.service_id === 'siding' && sidingType !== 'standard' 
                                  ? 'text-amber-600' 
                                  : 'text-slate-500'
                            }`}>
                              {serviceNote}
                            </p>
                          )}
                          {selectedCount >= 2 && price?.savings > 0 && (
                            <p className="text-xs text-emerald-600 font-medium">
                              Bundle savings: ${price.savings}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        {price && !price.requiresQuote && (
                          <>
                            {selectedCount >= 2 && price.savings > 0 ? (
                              <>
                                <p className="text-sm text-slate-400 line-through">${price.original}</p>
                                <p className="text-lg font-bold text-blue-600">${price.discounted}</p>
                              </>
                            ) : (
                              <p className="text-lg font-bold text-slate-900">${price.original}</p>
                            )}
                          </>
                        )}
                        {price?.requiresQuote && (
                          <p className="text-sm font-medium text-amber-600">Contact for quote</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Window Count Selector */}
                    {service.service_id === 'windows' && isSelected && (
                      <div className="mt-4 pt-4 border-t border-slate-200" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                          <Label className="text-sm text-slate-700">Number of Windows:</Label>
                          <div className="flex items-center gap-3">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setWindowCount(prev => Math.max(prev - 1, 1));
                              }}
                              disabled={windowCount <= 1}
                              className="h-8 w-8 p-0"
                              data-testid="window-count-minus"
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="text-lg font-bold w-12 text-center" data-testid="window-count-value">
                              {windowCount}
                            </span>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setWindowCount(prev => Math.min(prev + 1, 100));
                              }}
                              disabled={windowCount >= 100}
                              className="h-8 w-8 p-0"
                              data-testid="window-count-plus"
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500 mt-2">
                          {windowCount} windows x ${getWindowPricePerUnit(windowCount)}/each = ${windowCount * getWindowPricePerUnit(windowCount)}
                        </p>
                        <p className="text-xs text-blue-600 mt-1">
                          Pricing: 1-15 @ $15 | 16-25 @ $12 | 26+ @ $10 per window
                        </p>
                      </div>
                    )}
                    
                    {/* Walkway/Entries Linear Feet Input */}
                    {service.service_id === 'entries' && isSelected && (
                      <div className="mt-4 pt-4 border-t border-slate-200" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                          <Label className="text-sm text-slate-700">Linear Feet of Walkway:</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="1"
                              max="500"
                              value={walkwayLinearFeet}
                              onChange={(e) => setWalkwayLinearFeet(Math.max(1, Math.min(500, parseInt(e.target.value) || 1)))}
                              className="w-20 text-center"
                              data-testid="walkway-lf-input"
                            />
                            <span className="text-sm text-slate-500">LF</span>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500 mt-2">
                          Pricing: ≤50' = $75 | 51'-100' = $100 | 101'-150' = $125 | 151'-200' = $150 | 200'+ = $175
                        </p>
                      </div>
                    )}
                    
                    {/* Sidewalk & Street Gutter Linear Feet Input */}
                    {service.service_id === 'sidewalk' && isSelected && (
                      <div className="mt-4 pt-4 border-t border-slate-200" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                          <Label className="text-sm text-slate-700">Linear Feet of Sidewalk:</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="1"
                              max="500"
                              value={sidewalkLinearFeet}
                              onChange={(e) => setSidewalkLinearFeet(Math.max(1, Math.min(500, parseInt(e.target.value) || 1)))}
                              className="w-20 text-center"
                              data-testid="sidewalk-lf-input"
                            />
                            <span className="text-sm text-slate-500">LF</span>
                          </div>
                        </div>
                        <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Additional charges apply for stain removal
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          Pricing: ≤50' = $75 | 51'-100' = $100 | 101'-150' = $125 | 151'-200' = $150 | 200'+ = $175
                        </p>
                      </div>
                    )}
                    
                    {/* Fence Linear Feet Selector */}
                    {service.service_id === 'fence' && isSelected && (
                      <div className="mt-4 pt-4 border-t border-slate-200" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                          <Label className="text-sm text-slate-700">Fence Linear Feet:</Label>
                          <div className="flex items-center gap-3">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setFenceLinearFeet(prev => Math.max(prev - 25, 25));
                              }}
                              disabled={fenceLinearFeet <= 25}
                              className="h-8 w-8 p-0"
                              data-testid="fence-lf-minus"
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="text-lg font-bold w-16 text-center" data-testid="fence-lf-value">
                              {fenceLinearFeet} LF
                            </span>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setFenceLinearFeet(prev => Math.min(prev + 25, 500));
                              }}
                              disabled={fenceLinearFeet >= 500}
                              className="h-8 w-8 p-0"
                              data-testid="fence-lf-plus"
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500 mt-2">
                          Pricing: ≤100 LF = $75 | 101-200 = $125 | 201-300 = $175 | 301-400 = $200 | 400+ = $325
                        </p>
                      </div>
                    )}
                    
                    {/* Patio Size Selector */}
                    {service.service_id === 'patio' && isSelected && (
                      <div className="mt-4 pt-4 border-t border-slate-200" onClick={(e) => e.stopPropagation()}>
                        <Label className="text-sm text-slate-700">Patio Size:</Label>
                        <Select value={patioSize} onValueChange={(val) => setPatioSize(val)}>
                          <SelectTrigger className="mt-2" data-testid="patio-size-select">
                            <SelectValue placeholder="Select patio size" />
                          </SelectTrigger>
                          <SelectContent>
                            {PATIO_SIZES.map((size) => (
                              <SelectItem key={size.value} value={size.value}>
                                {size.label} {size.price > 0 && `- $${size.price}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    {/* Deck Size Selector */}
                    {service.service_id === 'deck' && isSelected && (
                      <div className="mt-4 pt-4 border-t border-slate-200" onClick={(e) => e.stopPropagation()}>
                        <Label className="text-sm text-slate-700">Deck Size:</Label>
                        <Select value={deckSize} onValueChange={(val) => setDeckSize(val)}>
                          <SelectTrigger className="mt-2" data-testid="deck-size-select">
                            <SelectValue placeholder="Select deck size" />
                          </SelectTrigger>
                          <SelectContent>
                            {DECK_SIZES.map((size) => (
                              <SelectItem key={size.value} value={size.value}>
                                {size.label} {size.price > 0 && `- $${size.price}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    {/* Chimney Condition Selector */}
                    {service.service_id === 'chimney' && isSelected && (
                      <div className="mt-4 pt-4 border-t border-slate-200" onClick={(e) => e.stopPropagation()}>
                        <Label className="text-sm text-slate-700">Chimney Size & Condition:</Label>
                        <Select value={chimneyCondition} onValueChange={(val) => setChimneyCondition(val)}>
                          <SelectTrigger className="mt-2" data-testid="chimney-condition-select">
                            <SelectValue placeholder="Select condition" />
                          </SelectTrigger>
                          <SelectContent>
                            {CHIMNEY_CONDITIONS.map((cond) => (
                              <SelectItem key={cond.value} value={cond.value}>
                                {cond.label} {cond.price > 0 && `- $${cond.price}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}

          {/* Bundle Suggestions */}
          {selectedCount === 1 && getBundleSuggestions().length > 0 && (
            <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-white border border-blue-100 rounded-xl">
              <p className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Add these for extra savings:
              </p>
              {getBundleSuggestions().map((suggestion) => (
                <button
                  key={suggestion.service_id}
                  onClick={() => toggleService(suggestion.service_id)}
                  className="w-full text-left p-3 bg-white rounded-lg border border-blue-200 hover:border-blue-400 transition-colors mb-2"
                  data-testid={`bundle-suggestion-${suggestion.service_id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-900">{suggestion.service_name}</span>
                    <span className="text-blue-600 font-bold">Only ${suggestion.bundlePrice} more</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
